import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Modal, Form, Input, InputNumber, message, Spin, Tag, Image, Select, Upload, Card, Row, Col, Radio, Divider, Tooltip, Tabs, Pagination, Progress } from 'antd';
import type { InputNumberProps, PaginationProps, UploadFile } from 'antd';
import type { RcFile } from 'rc-upload/lib/interface';
import { PlusOutlined, EditOutlined, DeleteOutlined, LoadingOutlined, UploadOutlined, QuestionCircleOutlined, TranslationOutlined } from '@ant-design/icons';
import { getProducts, Product, ProductImage, updateProduct, createProduct, deleteProduct, getCategories, deleteCategory, updateCategory, createCategory } from '../../services/productService';
import { uploadProductImage } from '../../services/uploadService';
import { translateProduct } from '../../services/translationService';
import styled from 'styled-components';
import { SIZE_TEMPLATES, COMPARISON_TEMPLATES, CATEGORIES } from '../../constants/categories';
import { Category } from '../../types/product';
import { parseProductInfo } from '../../services/productParserService';
import { batchUploadProducts, BatchProductItem } from '../../services/batchUploadService';

const StyledImage = styled(Image)`
  width: 100%;
  height: 200px;
  object-fit: contain;
  background: #f5f5f5;
  border-radius: 4px 4px 0 0;
`;

const ProductCard = styled(Card)`
  height: 100%;
  display: flex;
  flex-direction: column;
  
  .ant-card-cover {
    height: 200px;
    overflow: hidden;
    background: #f5f5f5;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .ant-card-body {
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: 12px;
  }

  .ant-card-meta {
    flex: 1;
    margin-bottom: 16px;
  }

  .ant-card-actions {
    border-top: 1px solid #f0f0f0;
    background: #fafafa;
    
    @media (max-width: 576px) {
      li {
        margin: 4px;
      }
      
      .ant-btn {
        padding: 4px 8px;
        height: 28px;
        font-size: 12px;
        
        .anticon {
          font-size: 12px;
        }
      }
    }
  }

  @media (max-width: 576px) {
    .ant-card-cover {
      height: 140px;
    }

    .ant-card-body {
      padding: 8px;
    }
  }
`;

const ProductTitle = styled.div`
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 8px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;

  @media (max-width: 576px) {
    font-size: 14px;
    margin-bottom: 4px;
    -webkit-line-clamp: 1;
  }
`;

const ProductTags = styled.div`
  margin-bottom: 8px;
  min-height: 22px;
  .ant-tag {
    margin-bottom: 4px;
  }

  @media (max-width: 576px) {
    margin-bottom: 4px;
    min-height: 20px;
    
    .ant-tag {
      margin: 0 2px 2px 0;
      padding: 0 4px;
      font-size: 10px;
      line-height: 18px;
    }
  }
`;

const ProductPrice = styled.div`
  color: #D4303B;
  font-size: 16px;
  font-weight: bold;

  @media (max-width: 576px) {
    font-size: 14px;
  }
`;

const ActionBar = styled.div`
  margin-bottom: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  background: #fff;
  padding: 16px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

  @media (max-width: 576px) {
    flex-direction: column;
    align-items: stretch;
    padding: 12px;
    gap: 12px;
    
    .ant-space {
      display: flex;
      justify-content: flex-start;
      gap: 8px !important;
      
      .ant-btn {
        flex: none;
        width: auto;
      }
    }
    
    .ant-radio-group {
      display: flex;
      
      .ant-radio-button-wrapper {
        flex: 1;
        text-align: center;
        padding: 0 8px;
        font-size: 12px;
      }
    }
  }
`;

const LoadingContainer = styled.div`
  text-align: center;
  padding: 40px;
  background: #fff;
  border-radius: 8px;
  margin: 16px 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

  @media (max-width: 576px) {
    padding: 24px;
    margin: 12px 0;
  }
`;

const StyledModal = styled(Modal)`
  @media (max-width: 576px) {
    max-width: calc(100vw - 32px);
    
    .ant-modal-content {
      padding: 12px;
    }
    
    .ant-form-item {
      margin-bottom: 12px;
    }

    .ant-row {
      margin-left: -8px !important;
      margin-right: -8px !important;
    }

    .ant-col {
      padding-left: 8px !important;
      padding-right: 8px !important;
    }

    .ant-form-item-label {
      padding-bottom: 4px;
    }

    .ant-space {
      gap: 8px !important;
    }

    .ant-divider {
      margin: 16px 0;
      
      .ant-divider-inner-text {
        font-size: 14px;
      }
    }

    .ant-input-number,
    .ant-select,
    .ant-input,
    .ant-input-textarea {
      font-size: 14px;
    }

    .ant-form-item-extra {
      font-size: 12px;
      margin-top: 4px;
    }
  }
`;

const CompactFormItem = styled(Form.Item)`
  @media (max-width: 576px) {
    .ant-space-compact {
      display: flex;
      flex-direction: column;
      width: 100%;
      gap: 8px;
      
      .ant-form-item {
        width: 100% !important;
      }
    }
  }
`;

const ImageUploadContainer = styled.div`
  .image-list {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 12px;
    margin-top: 16px;
  }

  .image-item {
    position: relative;
    border-radius: 4px;
    overflow: hidden;
    background: #f5f5f5;
    
    &.is-main {
      border: 2px solid #1890ff;
    }
    
    .ant-image {
      width: 100%;
      aspect-ratio: 1;
      
      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }
    
    .image-actions {
      position: absolute;
      top: 4px;
      right: 4px;
      display: flex;
      gap: 4px;
      background: rgba(255, 255, 255, 0.8);
      border-radius: 4px;
      padding: 2px;
    }
  }

  @media (max-width: 576px) {
    .image-list {
      grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
      gap: 8px;
    }

    .image-actions {
      .ant-btn {
        padding: 2px 4px;
        font-size: 12px;
      }
    }
  }
`;

const StyledTabs = styled(Tabs)`
  .ant-tabs-nav {
    margin-bottom: 16px;
    padding: 0;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }

  .ant-tabs-tab {
    padding: 12px 24px;
  }

  @media (max-width: 576px) {
    .ant-tabs-tab {
      padding: 8px 16px;
    }
  }
`;

const CategoryCard = styled(Card)`
  height: 100%;
  
  .ant-card-cover {
    padding: 12px;
    
    img {
      border-radius: 8px;
      height: 160px;
      object-fit: cover;
    }
  }

  .ant-card-body {
    padding: 16px;
  }

  .category-title {
    font-size: 16px;
    font-weight: 500;
    margin-bottom: 8px;
  }

  .category-description {
    color: #666;
    margin-bottom: 16px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .category-id {
    color: #999;
    font-size: 12px;
  }

  @media (max-width: 576px) {
    .ant-card-cover img {
      height: 120px;
    }
  }
`;

const StyledTableImage = styled(Image)`
  width: 80px;
  height: 80px;
  object-fit: contain;
  background: #f5f5f5;
  border-radius: 4px;

  @media (max-width: 576px) {
    width: 60px;
    height: 60px;
  }
`;

interface TempImage extends Omit<ProductImage, 'url'> {
  url: string;
  file?: File;
  isLocal?: boolean;
}

type CategoryType = 'clothing' | 'sneakers';

interface ProductWithTranslations extends Product {
  translations?: {
    title?: string;
    description?: string;
    tags?: string[];
    comparison?: {
      original?: {
        material?: string;
        craftsmanship?: string;
      };
      replica?: {
        material?: string;
        craftsmanship?: string;
      };
    };
  };
}

const Products: React.FC = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [categoryForm] = Form.useForm();
  const [editingProduct, setEditingProduct] = useState<ProductWithTranslations | null>(null);
  const [products, setProducts] = useState<ProductWithTranslations[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [tempImages, setTempImages] = useState<TempImage[]>([]);
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  const [translationLoading, setTranslationLoading] = useState(false);
  const [categories, setCategories] = useState(CATEGORIES);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [categoryModalVisible, setCategoryModalVisible] = useState(false);
  const [categoryImageFile, setCategoryImageFile] = useState<File | null>(null);
  const [categoryImagePreview, setCategoryImagePreview] = useState<string>('');
  const [parsingLoading, setParsingLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 12,
    total: 0
  });
  const [isBatchModalVisible, setIsBatchModalVisible] = useState(false);
  const [batchProducts, setBatchProducts] = useState<BatchProductItem[]>([]);
  const [batchUploadLoading, setBatchUploadLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async (page = 1, pageSize = 12) => {
    try {
      setLoading(true);
      const data = await getProducts({
        page,
        pageSize,
        category: 'all'  // 在管理界面默认加载所有分类
      });
      
      console.log('Admin - Fetched products:', data);
      
      // 更新商品列表和分页信息
      setProducts(data.products || []);
      setPagination({
        current: page,
        pageSize: pageSize,
        total: data.totalProducts || 0
      });
    } catch (error) {
      console.error('Admin - Error fetching products:', error);
      message.error('加载商品列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 处理分页变化
  const handlePageChange = (page: number, pageSize?: number) => {
    fetchProducts(page, pageSize);
  };

  // 处理每页显示数量变化
  const handleShowSizeChange = (current: number, size: number) => {
    fetchProducts(1, size);
  };

  // 修改表格组件的分页配置
  const paginationConfig: PaginationProps = {
    ...pagination,
    onChange: handlePageChange,
    onShowSizeChange: handleShowSizeChange,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total: number) => `共 ${total} 条数据`,
    size: window.innerWidth <= 576 ? 'small' : 'default',
    pageSizeOptions: ['12', '24', '36', '48']
  };

  const columns = [
    {
      title: '商品图片',
      key: 'image',
      width: 100,
      render: (record: ProductWithTranslations) => (
        <StyledTableImage
          src={record.images.find(img => img.type === 'main')?.url}
          alt={record.title}
          fallback="https://via.placeholder.com/80x80?text=No+Image"
        />
      ),
    },
    {
      title: '商品名称',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
      width: 200,
    },
    {
      title: '价格',
      dataIndex: 'price',
      key: 'price',
      width: 120,
      render: (price: number, record: ProductWithTranslations) => `${record.currency} ${price}`,
    },
    {
      title: '分类',
      dataIndex: 'category',
      key: 'category',
      width: 100,
      render: (category: string) => (
        <Tag color={category === 'sneakers' ? 'blue' : 'green'}>
          {category === 'sneakers' ? '球鞋' : '服饰'}
        </Tag>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => (
        <Tag color={status === 'active' ? 'success' : 'default'}>
          {status === 'active' ? '在售' : '下架'}
        </Tag>
      ),
    },
    {
      title: '标签',
      key: 'tags',
      dataIndex: 'tags',
      width: 200,
      ellipsis: true,
      render: (tags: string[]) => (
        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
          {tags.slice(0, 3).map(tag => (
            <Tag key={tag}>{tag}</Tag>
          ))}
          {tags.length > 3 && <Tag>+{tags.length - 3}</Tag>}
        </div>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 160,
      render: (_: any, record: ProductWithTranslations) => (
        <Space size="small">
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Button
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  const handleAdd = async () => {
    await loadCategories(); // 重新加载分类数据
    setEditingProduct(null);
    setTempImages([]);
    form.resetFields();
    form.setFieldsValue({
      status: 'active',
      currency: 'CNY',
    });
    setIsModalVisible(true);
  };

  const handleEdit = async (product: ProductWithTranslations) => {
    await loadCategories(); // 重新加载分类数据
    setEditingProduct(product);
    setTempImages(product.images);
    form.setFieldsValue({
      title: product.title,
      price: product.price,
      category: product.category,
      description: product.description,
      tags: product.tags.join(', '),
      status: product.status,
      currency: product.currency,
      sizes: product.sizes.join(', '),
      comparison: {
        original: {
          price: product.comparison.original.price,
          material: product.comparison.original.material,
          craftsmanship: product.comparison.original.craftsmanship
        },
        replica: {
          price: product.comparison.replica.price,
          material: product.comparison.replica.material,
          craftsmanship: product.comparison.replica.craftsmanship
        }
      },
      translations: {
        title: product.translations?.title || '',
        description: product.translations?.description || '',
        tags: product.translations?.tags ? 
          (Array.isArray(product.translations.tags) ? 
            product.translations.tags.join(', ') : 
            product.translations.tags
          ) : '',
        comparison: {
          original: {
            material: product.translations?.comparison?.original?.material || '',
            craftsmanship: product.translations?.comparison?.original?.craftsmanship || ''
          },
          replica: {
            material: product.translations?.comparison?.replica?.material || '',
            craftsmanship: product.translations?.comparison?.replica?.craftsmanship || ''
          }
        }
      }
    });
    setIsModalVisible(true);
  };

  const handleDelete = (product: ProductWithTranslations) => {
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除商品 "${product.title}" 吗？删除后无法恢复。`,
      okText: '确认删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          setLoading(true);
          const success = await deleteProduct(product.id);
          
          if (success) {
            message.success('删除成功');
            await fetchProducts(); // 重新加载数据
          } else {
            message.error('删除失败');
          }
        } catch (error) {
          console.error('Error deleting product:', error);
          message.error('删除失败');
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const handleImageSelect = async (file: File) => {
    try {
      const localUrl = URL.createObjectURL(file);
      
      setTempImages(prev => {
        const newImage: TempImage = {
          url: localUrl,
          type: prev.length === 0 ? 'main' as const : 'detail' as const,
          alt: file.name,
          file: file,
          isLocal: true
        };
        return [...prev, newImage];
      });
      
      return false;
    } catch (error) {
      console.error('Error handling image select:', error);
      message.error('图片处理失败');
      return false;
    }
  };

  const uploadAllImages = async (productId: string) => {
    const uploadPromises = tempImages
      .filter(img => img.isLocal && img.file)
      .map(async (img) => {
        try {
          const uploadedImage = await uploadProductImage(productId, img.file!, img.type);
          if (!uploadedImage) {
            throw new Error('图片上传失败');
          }
          return uploadedImage;
        } catch (error) {
          console.error('Error uploading image:', error);
          throw error;
        }
      });

    try {
      const uploadedImages = await Promise.all(uploadPromises);
      return tempImages
        .filter(img => !img.isLocal)
        .concat(uploadedImages)
        .sort((a, b) => {
          if (a.type === 'main') return -1;
          if (b.type === 'main') return 1;
          return 0;
        });
    } catch (error) {
      throw new Error('部分图片上传失败');
    }
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      
      if (tempImages.length === 0) {
        message.error('请至少上传一张商品图片');
        return;
      }

      setLoading(true);
      const tags = values.tags.split(',').map((tag: string) => tag.trim()).filter((tag: string) => tag);
      
      // 如果是编辑模式，使用现有的商品ID
      // 如果是创建模式，生成新的商品ID
      const productId = editingProduct ? editingProduct.id : `product-${Date.now()}`;
      
      // 先上传所有图片，使用同一个商品ID
      const finalImages = await uploadAllImages(productId);
      
      const baseProductData = {
        title: values.title,
        price: values.price,
        category: values.category,
        description: values.description,
        tags: tags,
        status: values.status,
        currency: values.currency,
        images: finalImages,
        sizes: values.sizes.split(',').map((size: string) => size.trim()),
        materials: [],
        comparison: {
          original: {
            price: values.comparison.original.price,
            material: values.comparison.original.material,
            craftsmanship: values.comparison.original.craftsmanship
          },
          replica: {
            price: values.comparison.replica.price,
            material: values.comparison.replica.material,
            craftsmanship: values.comparison.replica.craftsmanship
          }
        },
        translations: values.translations
      };

      if (editingProduct) {
        const updateData = {
          ...editingProduct,
          ...baseProductData,
          updatedAt: new Date().toISOString()
        };

        const success = await updateProduct(editingProduct.id, updateData);
        
        if (success) {
          message.success('更新成功');
          setIsModalVisible(false);
          await fetchProducts();
        } else {
          message.error('更新失败');
        }
      } else {
        const newProductData: ProductWithTranslations = {
          ...baseProductData,
          id: productId,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };

        const createdProductId = await createProduct(newProductData);
        
        if (createdProductId) {
          message.success('创建成功');
          setIsModalVisible(false);
          await fetchProducts();
        } else {
          message.error('创建失败');
        }
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      message.error(error instanceof Error ? error.message : '操作失败');
    } finally {
      setLoading(false);
    }
  };

  const handleSetMainImage = (imageUrl: string) => {
    setTempImages(prev => {
      const newImages = prev.map(img => ({
        ...img,
        type: img.url === imageUrl ? 'main' as const : 'detail' as const
      }));
      return newImages;
    });
  };

  const handleRemoveImage = (imageUrl: string) => {
    setTempImages(prev => {
      const imageToRemove = prev.find(img => img.url === imageUrl);
      if (imageToRemove?.isLocal) {
        URL.revokeObjectURL(imageUrl);
      }
      
      const newImages = prev.filter(img => img.url !== imageUrl);
      if (newImages.length > 0 && !newImages.some(img => img.type === 'main')) {
        newImages[0].type = 'main';
      }
      return newImages;
    });
  };

  useEffect(() => {
    return () => {
      tempImages.forEach(img => {
        if (img.isLocal) {
          URL.revokeObjectURL(img.url);
        }
      });
    };
  }, []);

  const ProductGrid = () => (
    <>
      <Row gutter={[12, 12]}>
        {products.map(product => {
          // 确保 tags 始终是数组
          let processedTags: string[] = [];
          
          console.log('Product before processing:', {
            id: product.id,
            tags: product.tags,
            type: typeof product.tags,
            isArray: Array.isArray(product.tags)
          });
          
          try {
            if (Array.isArray(product.tags)) {
              processedTags = [...product.tags];
            } else if (typeof product.tags === 'string') {
              processedTags = (product.tags as string).split(',').map((tag: string) => tag.trim());
            } else if (product.tags) {
              processedTags = (String(product.tags) as string).split(',').map((tag: string) => tag.trim());
            }
          } catch (error) {
            console.error('Error processing tags:', error);
            processedTags = [];
          }
          
          console.log('Processed tags:', {
            id: product.id,
            originalTags: product.tags,
            processedTags: processedTags
          });

          return (
            <Col xs={24} sm={12} md={8} lg={6} key={product.id}>
              <ProductCard
                hoverable
                cover={
                  <StyledImage
                    src={product.images.find(img => img.type === 'main')?.url}
                    alt={product.title}
                    fallback="https://via.placeholder.com/200x200?text=No+Image"
                    preview={false}
                  />
                }
                actions={[
                  <Button
                    type="primary"
                    icon={<EditOutlined />}
                    onClick={() => handleEdit(product)}
                    block
                  >
                    编辑
                  </Button>,
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => handleDelete(product)}
                    block
                  >
                    删除
                  </Button>
                ]}
              >
                <ProductTitle>{product.title}</ProductTitle>
                <ProductTags>
                  {processedTags && processedTags.length > 0 ? (
                    <>
                      {processedTags.slice(0, 2).map((tag: string) => (
                        <Tag key={tag}>{tag}</Tag>
                      ))}
                      {processedTags.length > 2 && (
                        <Tag>+{processedTags.length - 2}</Tag>
                      )}
                    </>
                  ) : (
                    <Tag>无标签</Tag>
                  )}
                </ProductTags>
                <ProductPrice>¥{product.price}</ProductPrice>
                <Tag 
                  color={product.status === 'active' ? 'success' : 'default'}
                  style={{ marginTop: '4px' }}
                >
                  {product.status === 'active' ? '在售' : '下架'}
                </Tag>
              </ProductCard>
            </Col>
          );
        })}
      </Row>
      <div style={{ 
        marginTop: '24px', 
        textAlign: 'center',
        background: '#fff',
        padding: '16px',
        borderRadius: '8px'
      }}>
        <Pagination {...paginationConfig} />
      </div>
    </>
  );

  // 修改尺码模板选择处理函数
  const handleSizeTemplateSelect = (template: keyof typeof SIZE_TEMPLATES) => {
    form.setFieldValue('sizes', SIZE_TEMPLATES[template].join(', '));
  };

  // 修改价格对比模板处理函数
  const handleComparisonTemplateSelect = (category: keyof typeof COMPARISON_TEMPLATES) => {
    if (!category || !(category in COMPARISON_TEMPLATES)) return;
    
    const template = COMPARISON_TEMPLATES[category];
    const price = form.getFieldValue('price') || 0;
    
    form.setFieldsValue({
      comparison: {
        original: {
          price: price * 2,
          material: template.original.material,
          craftsmanship: template.original.craftsmanship
        },
        replica: {
          price: price,
          material: template.replica.material,
          craftsmanship: template.replica.craftsmanship
        }
      }
    });
  };

  // 监听价格变化，自动更新价格对比
  const handlePriceChange = (value: number | string | null) => {
    if (!value) return;
    const numericValue = typeof value === 'string' ? Number(value) : value;
    const category = form.getFieldValue('category');
    if (category) {
      handleComparisonTemplateSelect(category);
    }
  };

  const priceFormatter: InputNumberProps['formatter'] = (value, info) => {
    if (!value && value !== 0) return '';
    return `¥ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  const priceParser: InputNumberProps['parser'] = (value) => {
    const parsed = value ? value.replace(/\¥\s?|(,*)/g, '') : '0';
    return Number(parsed);
  };

  // 修改分类管理相关函数
  const handleAddCategory = () => {
    setEditingCategory(null);
    categoryForm.resetFields();
    setCategoryModalVisible(true);
  };

  const handleEditCategory = (category: Category) => {
    setEditingCategory(category);
    categoryForm.setFieldsValue({
      categoryId: category.id,
      categoryName: category.name,
      categoryDescription: category.description,
      categoryOrder: category.order,
      categoryImage: category.image,
      categoryTranslations: category.translations
    });
    setCategoryImagePreview(category.image);
    setCategoryModalVisible(true);
  };

  const handleDeleteCategory = async (category: Category) => {
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除分类 "${category.name}" 吗？删除后无法恢复。`,
      okText: '确认删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          const success = await deleteCategory(category.id);
          if (success) {
            message.success('删除成功');
            // 重新加载分类列表
            const updatedCategories = await getCategories();
            setCategories(updatedCategories);
            // 触发分类列表更新事件
            window.localStorage.setItem('categoryListUpdated', Date.now().toString());
            window.dispatchEvent(new Event('storage'));
          } else {
            message.error('删除失败');
          }
        } catch (error) {
          console.error('Error deleting category:', error);
          message.error('删除失败');
        }
      }
    });
  };

  const handleCategoryModalOk = async () => {
    try {
      const values = await categoryForm.validateFields();
      const categoryData = {
        id: values.categoryId,
        name: values.categoryName,
        description: values.categoryDescription,
        order: values.categoryOrder,
        image: values.categoryImage,
        translations: values.categoryTranslations
      };

      let success;
      if (editingCategory) {
        success = await updateCategory(editingCategory.id, categoryData, categoryImageFile || undefined);
      } else {
        success = await createCategory(categoryData, categoryImageFile || undefined);
      }

      if (success) {
        message.success(editingCategory ? '更新成功' : '创建成功');
        const updatedCategories = await getCategories();
        setCategories(updatedCategories);
        window.localStorage.setItem('categoryListUpdated', Date.now().toString());
        window.dispatchEvent(new Event('storage'));
        setCategoryModalVisible(false);
        setCategoryImageFile(null);
        setCategoryImagePreview('');
        categoryForm.resetFields();
      } else {
        message.error(editingCategory ? '更新失败' : '创建失败');
      }
    } catch (error) {
      console.error('表单验证失败:', error);
    }
  };

  // 添加分类数据加载函数
  const loadCategories = async () => {
    try {
      const categoriesData = await getCategories();
      setCategories(categoriesData);
    } catch (error) {
      console.error('Error loading categories:', error);
      message.error('加载分类失败');
    }
  };

  // 在组件加载时获取分类数据
  useEffect(() => {
    loadCategories();
  }, []);

  const CategoryManagement = () => (
    <div>
      <ActionBar>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAddCategory}>
          添加分类
        </Button>
      </ActionBar>

      <Row gutter={[16, 16]}>
        {categories.map(category => (
          <Col xs={24} sm={12} md={8} lg={6} key={category.id}>
            <CategoryCard
              cover={<img alt={category.name} src={category.image} />}
              actions={[
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  onClick={() => handleEditCategory(category)}
                >
                  编辑
                </Button>,
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => handleDeleteCategory(category)}
                >
                  删除
                </Button>
              ]}
            >
              <div className="category-title">{category.name}</div>
              <div className="category-description">{category.description}</div>
              <div className="category-id">ID: {category.id}</div>
              <div className="category-id">排序: {category.order}</div>
            </CategoryCard>
          </Col>
        ))}
      </Row>

      <Modal
        title={editingCategory ? '编辑分类' : '添加分类'}
        open={categoryModalVisible}
        onOk={handleCategoryModalOk}
        onCancel={() => {
          setCategoryModalVisible(false);
          setCategoryImageFile(null);
          setCategoryImagePreview('');
          categoryForm.resetFields();
        }}
        width={720}
      >
        <Form form={categoryForm} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="categoryId"
                label="分类ID"
                rules={[
                  { required: true, message: '请输入分类ID' },
                  { pattern: /^[a-zA-Z0-9-]+$/, message: '只能包含字母、数字和连字符' }
                ]}
              >
                <Input placeholder="例如：sneakers" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="categoryOrder"
                label="排序"
                rules={[{ required: true, message: '请输入排序数字' }]}
              >
                <Input type="number" placeholder="数字越小越靠前" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="categoryName"
                label="分类名称"
                rules={[{ required: true, message: '请输入分类名称' }]}
              >
                <Input placeholder="例如：球鞋" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name={['categoryTranslations', 'en', 'name']}
                label="英文名称"
                rules={[{ required: true, message: '请输入英文名称' }]}
              >
                <Input placeholder="例如：Sneakers" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="categoryDescription"
                label="描述"
                rules={[{ required: true, message: '请输入描述' }]}
              >
                <Input.TextArea rows={4} placeholder="请输入分类描述" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name={['categoryTranslations', 'en', 'description']}
                label="英文描述"
                rules={[{ required: true, message: '请输入英文描述' }]}
              >
                <Input.TextArea rows={4} placeholder="请输入英文描述" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="categoryImage"
            label="分类图片"
            extra="如果不上传，将使用默认图片"
          >
            <div style={{ marginBottom: '8px' }}>
              <Upload
                accept="image/*"
                showUploadList={false}
                beforeUpload={handleImageSelect}
              >
                <Button icon={<UploadOutlined />}>选择图片</Button>
              </Upload>
            </div>
            {(categoryImagePreview || form.getFieldValue('categoryImage')) && (
              <div style={{ marginTop: '8px' }}>
                <img
                  src={categoryImagePreview || form.getFieldValue('categoryImage')}
                  alt="分类图片预览"
                  style={{
                    maxWidth: '200px',
                    maxHeight: '200px',
                    objectFit: 'contain'
                  }}
                />
              </div>
            )}
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );

  // 添加智能提取处理函数
  const handleSmartParse = async () => {
    const rawText = form.getFieldValue('rawProductInfo');
    if (!rawText) {
      message.warning('请输入商品信息');
      return;
    }

    try {
      setParsingLoading(true);
      const parsedInfo = await parseProductInfo(rawText);
      console.log('Smart parse result:', parsedInfo);
      
      // 2. 翻译解析后的信息
      const translationResult = await translateProduct({
        title: parsedInfo.title,
        description: parsedInfo.description,
        tags: Array.isArray(parsedInfo.tags) ? parsedInfo.tags.join(', ') : '',
        comparison: {
          original: {
            material: parsedInfo.comparison.original.material,
            craftsmanship: parsedInfo.comparison.original.craftsmanship
          },
          replica: {
            material: parsedInfo.comparison.replica.material,
            craftsmanship: parsedInfo.comparison.replica.craftsmanship
          }
        }
      }, 'en');
      
      // 更新表单数据
      form.setFieldsValue({
        title: parsedInfo.title,
        description: parsedInfo.description,
        tags: Array.isArray(parsedInfo.tags) ? parsedInfo.tags.join(', ') : '',
        sizes: Array.isArray(parsedInfo.sizes) ? parsedInfo.sizes.join(', ') : '',
        category: parsedInfo.category,
        comparison: {
          original: {
            material: parsedInfo.comparison.original.material,
            craftsmanship: parsedInfo.comparison.original.craftsmanship
          },
          replica: {
            material: parsedInfo.comparison.replica.material,
            craftsmanship: parsedInfo.comparison.replica.craftsmanship
          }
        },
        translations: {
          title: translationResult.title || '',
          description: translationResult.description || '',
          tags: Array.isArray(translationResult.tags) ? translationResult.tags : [],
          comparison: {
            original: {
              material: translationResult.comparison?.original?.material || '',
              craftsmanship: translationResult.comparison?.original?.craftsmanship || ''
            },
            replica: {
              material: translationResult.comparison?.replica?.material || '',
              craftsmanship: translationResult.comparison?.replica?.craftsmanship || ''
            }
          }
        }
      });

      // 如果有货号，也填充到表单中
      if (parsedInfo.productCode) {
        form.setFieldValue('productCode', parsedInfo.productCode);
      }

      message.success('商品信息提取成功');
    } catch (error) {
      console.error('商品信息提取失败:', error);
      message.error('商品信息提取失败');
    } finally {
      setParsingLoading(false);
    }
  };

  // 添加批量上传处理函数
  const handleBatchUpload = () => {
    setBatchProducts([{ rawText: '', price: 0, images: [] }]);
    setIsBatchModalVisible(true);
  };

  // 处理添加新商品项
  const handleAddBatchItem = () => {
    setBatchProducts([...batchProducts, { rawText: '', price: 0, images: [] }]);
  };

  // 处理删除商品项
  const handleRemoveBatchItem = (index: number) => {
    setBatchProducts(batchProducts.filter((_, i) => i !== index));
  };

  // 处理商品项数据更新
  const handleBatchItemChange = (index: number, field: keyof BatchProductItem, value: any) => {
    const newProducts = [...batchProducts];
    newProducts[index] = { ...newProducts[index], [field]: value };
    setBatchProducts(newProducts);
  };

  // 处理批量图片上传
  const handleBatchImageSelect = async (index: number, fileList: RcFile[]) => {
    const newProducts = [...batchProducts];
    newProducts[index] = {
      ...newProducts[index],
      images: [...newProducts[index].images, ...fileList]
    };
    setBatchProducts(newProducts);
    return false;
  };

  // 处理批量上传确认
  const handleBatchModalOk = async () => {
    // 验证数据
    const invalidItems = batchProducts.filter(
      item => !item.rawText || item.price <= 0 || item.images.length === 0
    );

    if (invalidItems.length > 0) {
      message.error('请确保所有商品都填写了商品信息、价格，并上传了至少一张图片');
      return;
    }

    try {
      setBatchUploadLoading(true);
      const result = await batchUploadProducts(batchProducts, (current, total) => {
        setUploadProgress({ current, total });
      });

      if (result.success > 0) {
        message.success(`成功上传 ${result.success} 个商品`);
        if (result.failed > 0) {
          Modal.warning({
            title: '部分商品上传失败',
            content: (
              <div>
                <p>成功：{result.success} 个</p>
                <p>失败：{result.failed} 个</p>
                <div style={{ maxHeight: '200px', overflow: 'auto' }}>
                  {result.errors.map((error, index) => (
                    <p key={index}>
                      商品 {error.index + 1}: {error.error}
                    </p>
                  ))}
                </div>
              </div>
            ),
          });
        }
        setIsBatchModalVisible(false);
        fetchProducts();
      } else {
        message.error('批量上传失败');
      }
    } catch (error) {
      console.error('批量上传错误:', error);
      message.error('批量上传失败');
    } finally {
      setBatchUploadLoading(false);
      setUploadProgress({ current: 0, total: 0 });
    }
  };

  if (loading) {
    return (
      <LoadingContainer>
        <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
      </LoadingContainer>
    );
  }

  return (
    <div style={{ padding: '16px' }}>
      <StyledTabs
        defaultActiveKey="products"
        items={[
          {
            key: 'products',
            label: '商品管理',
            children: (
              <>
                <ActionBar>
                  <Space>
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                      添加商品
                    </Button>
                    <Button icon={<UploadOutlined />} onClick={handleBatchUpload}>
                      批量上传
                    </Button>
                  </Space>
                  <Radio.Group 
                    value={viewMode} 
                    onChange={e => setViewMode(e.target.value)} 
                    buttonStyle="solid"
                    size={window.innerWidth <= 576 ? 'small' : 'middle'}
                  >
                    <Radio.Button value="grid">网格视图</Radio.Button>
                    <Radio.Button value="table">表格视图</Radio.Button>
                  </Radio.Group>
                </ActionBar>

                {loading ? (
                  <LoadingContainer>
                    <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
                  </LoadingContainer>
                ) : viewMode === 'grid' ? (
                  <ProductGrid />
                ) : (
                  <div style={{ 
                    background: '#fff', 
                    padding: '16px', 
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                    overflowX: 'auto'
                  }}>
                    <Table 
                      columns={columns} 
                      dataSource={products} 
                      rowKey="id"
                      pagination={paginationConfig}
                      scroll={{ x: 1000 }}
                      size="small"
                    />
                  </div>
                )}

                {/* 商品编辑模态框 */}
                <StyledModal
                  title={editingProduct ? '编辑商品' : '添加商品'}
                  open={isModalVisible}
                  onOk={handleModalOk}
                  onCancel={() => {
                    setIsModalVisible(false);
                    setTempImages([]);
                  }}
                  width={800}
                  centered
                >
                  <Form form={form} layout="vertical">
                    <Card
                      style={{ marginBottom: 24 }}
                      title={
                        <Space>
                          <span>智能提取商品信息</span>
                          <Tooltip title="粘贴商品描述文本，系统将自动提取商品信息并填充表单（价格需要手动输入）">
                            <QuestionCircleOutlined />
                          </Tooltip>
                        </Space>
                      }
                    >
                      <Space direction="vertical" style={{ width: '100%' }}>
                        <Form.Item
                          name="rawProductInfo"
                          rules={[{ required: false }]}
                        >
                          <Input.TextArea
                            rows={6}
                            placeholder="请粘贴商品描述文本，例如：
Air Jordan 1 Low AJ1 乔1 复古黑蓝 运动鞋
产品描述：这款Air Jordan 1 Low以经典配色重新演绎，鞋面采用优质皮革材质，搭配网眼布，透气舒适。
标签：AJ1, 乔丹, 运动鞋, 复古
尺码：40,41,42,43,44,45
货号：553558-412
"
                          />
                        </Form.Item>
                        <Button
                          type="primary"
                          onClick={handleSmartParse}
                          loading={parsingLoading}
                          icon={<TranslationOutlined />}
                          block
                        >
                          智能提取商品信息
                        </Button>
                      </Space>
                    </Card>

                    <Divider orientation="left">基本信息</Divider>
                    
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item
                          name="title"
                          label="商品名称"
                          rules={[{ required: true, message: '请输入商品名称' }]}
                        >
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          name={['translations', 'title']}
                          label="商品名称（翻译）"
                        >
                          <Input placeholder="翻译后的商品名称" />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item
                          name="description"
                          label="描述"
                          rules={[{ required: true, message: '请输入商品描述' }]}
                        >
                          <Input.TextArea rows={4} />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          name={['translations', 'description']}
                          label="描述（翻译）"
                        >
                          <Input.TextArea rows={4} placeholder="翻译后的商品描述" />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item
                          name="tags"
                          label="标签"
                          rules={[{ required: true, message: '请输入标签，用逗号分隔' }]}
                        >
                          <Input placeholder="标签之间用逗号分隔" />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          name={['translations', 'tags']}
                          label="标签（翻译）"
                        >
                          <Input placeholder="翻译后的标签，用逗号分隔" />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Divider orientation="left">价格与状态</Divider>
                    
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item
                          name="price"
                          label="价格"
                          rules={[{ required: true, message: '请输入价格' }]}
                        >
                          <InputNumber
                            style={{ width: '100%' }}
                            formatter={priceFormatter}
                            parser={priceParser}
                            onChange={handlePriceChange}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          name="currency"
                          label="货币"
                          rules={[{ required: true, message: '请选择货币' }]}
                        >
                          <Select>
                            <Select.Option value="CNY">人民币 (CNY)</Select.Option>
                            <Select.Option value="USD">美元 (USD)</Select.Option>
                            <Select.Option value="EUR">欧元 (EUR)</Select.Option>
                            <Select.Option value="GBP">英镑 (GBP)</Select.Option>
                          </Select>
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item
                          name="category"
                          label="分类"
                          rules={[{ required: true, message: '请选择分类' }]}
                        >
                          <Select onChange={(value: string) => {
                            const category = categories.find(c => c.id === value);
                            if (category) {
                              handleComparisonTemplateSelect(value as CategoryType);
                              if (category.id === 'clothing') {
                                handleSizeTemplateSelect('clothing');
                              } else if (category.id === 'sneakers') {
                                handleSizeTemplateSelect('shoes');
                              }
                            }
                          }}>
                            {categories.map(category => (
                              <Select.Option key={category.id} value={category.id}>
                                {category.name}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          name="status"
                          label="状态"
                          rules={[{ required: true, message: '请选择商品状态' }]}
                        >
                          <Select>
                            <Select.Option value="active">在售</Select.Option>
                            <Select.Option value="inactive">下架</Select.Option>
                          </Select>
                        </Form.Item>
                      </Col>
                    </Row>

                    <Form.Item
                      name="sizes"
                      label={
                        <Space>
                          <span>可选尺码</span>
                          <Space.Compact size="small">
                            <Button onClick={() => handleSizeTemplateSelect('clothing')}>服饰尺码</Button>
                            <Button onClick={() => handleSizeTemplateSelect('shoes')}>鞋码</Button>
                            <Button onClick={() => handleSizeTemplateSelect('oneSize')}>均码</Button>
                          </Space.Compact>
                        </Space>
                      }
                      rules={[{ required: true, message: '请输入可选尺码，用逗号分隔' }]}
                    >
                      <Input.TextArea
                        autoSize={{ minRows: 1, maxRows: 3 }}
                        placeholder="尺码之间用逗号分隔，如: S,M,L,XL"
                      />
                    </Form.Item>

                    <Divider orientation="left">价格对比信息</Divider>
                    
                    <Row gutter={16}>
                      <Col span={12}>
                        <CompactFormItem label="原版信息">
                          <Space.Compact style={{ width: '100%' }}>
                            <Form.Item
                              name={['comparison', 'original', 'price']}
                              rules={[{ required: true, message: '请输入原版价格' }]}
                              style={{ marginBottom: 0, width: '33%' }}
                            >
                              <InputNumber
                                style={{ width: '100%' }}
                                placeholder="原版价格"
                                formatter={priceFormatter}
                                parser={priceParser}
                              />
                            </Form.Item>
                            <Form.Item
                              name={['comparison', 'original', 'material']}
                              rules={[{ required: true, message: '请输入原版材质' }]}
                              style={{ marginBottom: 0, width: '33%' }}
                            >
                              <Input placeholder="原版材质" />
                            </Form.Item>
                            <Form.Item
                              name={['comparison', 'original', 'craftsmanship']}
                              rules={[{ required: true, message: '请输入原版工艺' }]}
                              style={{ marginBottom: 0, width: '34%' }}
                            >
                              <Input placeholder="原版工艺" />
                            </Form.Item>
                          </Space.Compact>
                        </CompactFormItem>
                      </Col>
                      <Col span={12}>
                        <CompactFormItem label="原版信息（翻译）">
                          <Space.Compact style={{ width: '100%' }}>
                            <Form.Item
                              name={['translations', 'comparison', 'original', 'material']}
                              style={{ marginBottom: 0, width: '50%' }}
                            >
                              <Input placeholder="原版材质翻译" />
                            </Form.Item>
                            <Form.Item
                              name={['translations', 'comparison', 'original', 'craftsmanship']}
                              style={{ marginBottom: 0, width: '50%' }}
                            >
                              <Input placeholder="原版工艺翻译" />
                            </Form.Item>
                          </Space.Compact>
                        </CompactFormItem>
                      </Col>
                    </Row>

                    <Row gutter={16}>
                      <Col span={12}>
                        <CompactFormItem label="复刻信息">
                          <Space.Compact style={{ width: '100%' }}>
                            <Form.Item
                              name={['comparison', 'replica', 'price']}
                              rules={[{ required: true, message: '请输入复刻价格' }]}
                              style={{ marginBottom: 0, width: '33%' }}
                            >
                              <InputNumber
                                style={{ width: '100%' }}
                                placeholder="复刻价格"
                                formatter={priceFormatter}
                                parser={priceParser}
                              />
                            </Form.Item>
                            <Form.Item
                              name={['comparison', 'replica', 'material']}
                              rules={[{ required: true, message: '请输入复刻材质' }]}
                              style={{ marginBottom: 0, width: '33%' }}
                            >
                              <Input placeholder="复刻材质" />
                            </Form.Item>
                            <Form.Item
                              name={['comparison', 'replica', 'craftsmanship']}
                              rules={[{ required: true, message: '请输入复刻工艺' }]}
                              style={{ marginBottom: 0, width: '34%' }}
                            >
                              <Input placeholder="复刻工艺" />
                            </Form.Item>
                          </Space.Compact>
                        </CompactFormItem>
                      </Col>
                      <Col span={12}>
                        <CompactFormItem label="复刻信息（翻译）">
                          <Space.Compact style={{ width: '100%' }}>
                            <Form.Item
                              name={['translations', 'comparison', 'replica', 'material']}
                              style={{ marginBottom: 0, width: '50%' }}
                            >
                              <Input placeholder="复刻材质翻译" />
                            </Form.Item>
                            <Form.Item
                              name={['translations', 'comparison', 'replica', 'craftsmanship']}
                              style={{ marginBottom: 0, width: '50%' }}
                            >
                              <Input placeholder="复刻工艺翻译" />
                            </Form.Item>
                          </Space.Compact>
                        </CompactFormItem>
                      </Col>
                    </Row>

                    <Form.Item 
                      label="商品图片" 
                      extra="第一张图片将自动设为主图，点击图片右上角的星标可以将该图片设为主图"
                      rules={[{ required: true, message: '请上传至少一张商品图片' }]}
                    >
                      <ImageUploadContainer>
                        <Upload
                          accept="image/*"
                          showUploadList={false}
                          beforeUpload={handleImageSelect}
                          multiple
                        >
                          <Button icon={<UploadOutlined />}>
                            选择图片
                          </Button>
                        </Upload>

                        <div className="image-list">
                          {tempImages.map((image, index) => (
                            <div 
                              key={index} 
                              className={`image-item ${image.type === 'main' ? 'is-main' : ''}`}
                            >
                              <Image
                                src={image.url}
                                alt={`商品图片 ${index + 1}`}
                                width={200}
                              />
                              <div className="image-actions">
                                <Button
                                  type="text"
                                  size="small"
                                  icon={<EditOutlined />}
                                  title="设为主图"
                                  onClick={() => handleSetMainImage(image.url)}
                                />
                                <Button
                                  type="text"
                                  danger
                                  size="small"
                                  icon={<DeleteOutlined />}
                                  onClick={() => handleRemoveImage(image.url)}
                                />
                              </div>
                              {image.isLocal && (
                                <div 
                                  style={{ 
                                    position: 'absolute',
                                    bottom: 0,
                                    left: 0,
                                    right: 0,
                                    background: 'rgba(0,0,0,0.5)',
                                    color: '#fff',
                                    padding: '2px 8px',
                                    fontSize: '12px',
                                    textAlign: 'center'
                                  }}
                                >
                                  待上传
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </ImageUploadContainer>
                    </Form.Item>
                  </Form>
                </StyledModal>

                {/* 批量上传模态框 */}
                <StyledModal
                  title="批量上传商品"
                  open={isBatchModalVisible}
                  onOk={handleBatchModalOk}
                  onCancel={() => setIsBatchModalVisible(false)}
                  width={800}
                  confirmLoading={batchUploadLoading}
                >
                  <Space direction="vertical" style={{ width: '100%' }}>
                    {batchProducts.map((item, index) => (
                      <Card
                        key={index}
                        title={`商品 ${index + 1}`}
                        extra={
                          batchProducts.length > 1 && (
                            <Button
                              type="text"
                              danger
                              icon={<DeleteOutlined />}
                              onClick={() => handleRemoveBatchItem(index)}
                            >
                              删除
                            </Button>
                          )
                        }
                      >
                        <Space direction="vertical" style={{ width: '100%' }}>
                          <Input.TextArea
                            rows={6}
                            value={item.rawText}
                            onChange={e => handleBatchItemChange(index, 'rawText', e.target.value)}
                            placeholder="请输入商品描述文本"
                          />
                          <InputNumber
                            style={{ width: '100%' }}
                            value={item.price}
                            onChange={value => handleBatchItemChange(index, 'price', value)}
                            formatter={(value) => value ? `¥ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : '¥ 0'}
                            parser={(value) => Number(value!.replace(/\¥\s?|(,*)/g, ''))}
                            placeholder="请输入商品价格"
                          />
                          <Upload
                            accept="image/*"
                            multiple
                            beforeUpload={(file, fileList) => {
                              handleBatchImageSelect(index, fileList);
                              return false;
                            }}
                            fileList={item.images.map((file, i) => ({
                              uid: `-${i}`,
                              name: file.name,
                              status: 'done',
                              url: URL.createObjectURL(file),
                            }))}
                            onRemove={(file) => {
                              const newImages = item.images.filter(
                                (_, i) => i !== parseInt((file.uid || '0').replace('-', ''))
                              );
                              handleBatchItemChange(index, 'images', newImages);
                            }}
                          >
                            <Button icon={<UploadOutlined />}>选择图片</Button>
                          </Upload>
                        </Space>
                      </Card>
                    ))}

                    <Button
                      type="dashed"
                      onClick={handleAddBatchItem}
                      style={{ width: '100%' }}
                      icon={<PlusOutlined />}
                    >
                      添加商品
                    </Button>

                    {batchUploadLoading && (
                      <div style={{ textAlign: 'center' }}>
                        <Progress
                          percent={Math.round((uploadProgress.current / uploadProgress.total) * 100)}
                          status="active"
                        />
                        <div>正在处理：{uploadProgress.current}/{uploadProgress.total}</div>
                      </div>
                    )}
                  </Space>
                </StyledModal>
              </>
            ),
          },
          {
            key: 'categories',
            label: '分类管理',
            children: <CategoryManagement />,
          },
        ]}
      />
    </div>
  );
};

export default Products; 